import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
    Button, 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    Box, 
    Typography,
    IconButton,
    Divider,
    Tabs,
    Tab,
    Card,
    CardContent,
    Chip,
    Tooltip,
    Avatar,
    AvatarGroup,
    useTheme,
    useMediaQuery
} from '@mui/material';
import { 
    Add as AddIcon, 
    Close as CloseIcon,
    Groups as TeamIcon,
    Person as MemberIcon,
    Schedule as PlanningIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Work as EmployeeIcon,
    CalendarToday as CalendarIcon,
    EventBusy as AbsenceIcon
} from '@mui/icons-material';
import AddTeam from '../employee/team/addTeam';
import AddMember from '../employee/teamMember/addmember';
import Team from '../employee/team/team';
import Schedule from '../employee/schedule/schedule';
import TeamMembers from '../employee/teamMember/team_members';
import UpdateTeam from '../employee/team/updateTeam';
import DeleteTeam from '../employee/team/deleteTeam';
import useSequentialDialogs from '../../../hooks/useSequentialDialogs';
import theme from '../../../theme';
import {
    // useTeamsTeamsSalonTeamsRetrieveQuery, // TODO: Endpoint not generated
    useTeamsTeamMembersSalonMembersRetrieveQuery
} from '../../../services/GLOWClient';
import { useSalonAccess } from '../../../context/SalonAccessContext';
import EmployeeUnavailabilityPanel from '../employee/unavailability/EmployeeUnavailabilityPanel';

const MemberPlanningSectionSequential = ({ 
    onSuccess,
    onQuit,
    selectedSalonId = null 
}) => {
    const { t } = useTranslation();
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
    const isSmallMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
    const { hasPermission } = useSalonAccess();
    
    const [activeTab, setActiveTab] = useState(0);
    const [viewMode, setViewMode] = useState('employees'); // 'employees', 'plannings', 'teams'
    const [createdTeams, setCreatedTeams] = useState([]);
    const [createdMembers, setCreatedMembers] = useState([]);
    const [currentTeamForMember, setCurrentTeamForMember] = useState(null);
    const [showTeamManagement, setShowTeamManagement] = useState(false);
    const [selectedTeamForEdit, setSelectedTeamForEdit] = useState(null);
    const [selectedTeamForDelete, setSelectedTeamForDelete] = useState(null);
    const [isUpdateTeamDialogOpen, setIsUpdateTeamDialogOpen] = useState(false);
    const [isDeleteTeamDialogOpen, setIsDeleteTeamDialogOpen] = useState(false);
    const [selectedAbsenceMember, setSelectedAbsenceMember] = useState(null);
    const canManageAbsences = Boolean(
        selectedSalonId &&
        hasPermission(selectedSalonId, 'employees.absences.manage')
    );
    
    // TODO: Endpoint not generated - needs manual implementation
    const teamsData = [];
    const refetchTeams = () => {};
    // const { data: teamsData, refetch: refetchTeams } = useTeamsTeamsSalonTeamsRetrieveQuery(
    //     selectedSalonId ? { salonId: selectedSalonId } : {},
    //     { skip: !selectedSalonId }
    // );
    
    const { data: membersData, refetch: refetchMembers } = useTeamsTeamMembersSalonMembersRetrieveQuery(
        selectedSalonId ? { salon: selectedSalonId } : {},
        { skip: !selectedSalonId }
    );
    
    // Combine existing and newly created data
    const allTeams = [
        ...(Array.isArray(teamsData) ? teamsData : (teamsData?.results || [])),
        ...createdTeams
    ];
    
    const allMembers = [
        ...(Array.isArray(membersData) ? membersData : (membersData?.results || [])),
        ...createdMembers
    ];
    
    const { 
        currentDialog, 
        openDialog, 
        closeDialog, 
        closeAllDialogs 
    } = useSequentialDialogs();

    // Reset state on mount
    useEffect(() => {
        setActiveTab(0);
        setCreatedTeams([]);
        setCreatedMembers([]);
        setCurrentTeamForMember(null);
        closeAllDialogs();
    }, [closeAllDialogs]);
    
    // Refetch data when new items are created
    useEffect(() => {
        if (selectedSalonId) {
            refetchTeams();
            refetchMembers();
        }
    }, [selectedSalonId]);

    const handleTeamSuccess = (message, teamData) => {
        if (teamData) {
            const normalizedId = teamData.team_id || teamData.id || teamData.pk;
            const teamWithId = {
                ...teamData,
                team_id: normalizedId,
                // ensure salon is present for filtering in TeamMembers
                salon: teamData.salon || selectedSalonId,
            };
            setCreatedTeams(prev => {
                const dedup = prev.filter(t => String(t.team_id || t.id) !== String(normalizedId));
                return [...dedup, teamWithId];
            });
        }
        
        // Refetch backend data (updates RTK Query cache for salon teams)
        refetchTeams();
        
        closeDialog(); // Close the team dialog
        
        if (onSuccess) {
            onSuccess(message || t('pro_team_member.team_created'));
        }
    };

    const handleMemberSuccess = (message, memberData) => {
        if (memberData) {
            const memberWithId = {
                ...memberData,
                team_member_id: memberData.team_member_id || memberData.id || memberData.pk,
                user_details: memberData.user_details || memberData.user || {}
            };
            setCreatedMembers(prev => [...prev, memberWithId]);
            
            // Refetch backend data
            refetchMembers();
            
            // Instead of closing, open planning dialog for the new member
            openDialog({
                type: 'planning',
                props: {
                    open: true,
                    onClose: closeDialog,
                    member: memberWithId,
                    selectedSalonId
                }
            });
            
            if (onSuccess) {
                onSuccess(message || t('pro_team_member.member_created_with_planning'));
            }
        } else {
            // If no member data, just close the dialog
            closeDialog();
            
            if (onSuccess) {
                onSuccess(message || t('pro_team_member.member_created'));
            }
        }
    };

    const handleOpenTeamDialog = () => {
        openDialog({
            type: 'team',
            props: {
                open: true,
                onClose: closeDialog,
                onSuccess: handleTeamSuccess,
                selectedSalonId
            }
        });
    };

    const handleOpenMemberDialog = (team = null) => {
        setCurrentTeamForMember(team);
        openDialog({
            type: 'member',
            props: {
                open: true,
                onClose: closeDialog,
                onSuccess: handleMemberSuccess,
                selectedSalonId,
                preselectedTeam: team
            }
        });
    };

    const handleOpenTeamManagement = () => {
        setShowTeamManagement(true);
    };

    const handleCloseTeamManagement = () => {
        setShowTeamManagement(false);
    };

    const handleEditTeam = (team) => {
        setSelectedTeamForEdit(team);
        setIsUpdateTeamDialogOpen(true);
    };

    const handleDeleteTeam = (team) => {
        setSelectedTeamForDelete(team);
        setIsDeleteTeamDialogOpen(true);
    };

    const handleUpdateTeamSuccess = () => {
        setIsUpdateTeamDialogOpen(false);
        setSelectedTeamForEdit(null);
        // Sync locally created teams if the updated team was created in this session
        setCreatedTeams(prev => prev.map(t => {
            const id = t.team_id || t.id;
            const editId = selectedTeamForEdit?.team_id || selectedTeamForEdit?.id;
            if (String(id) === String(editId)) {
                return { ...t, name: selectedTeamForEdit?.name ?? t.name, description: selectedTeamForEdit?.description ?? t.description };
            }
            return t;
        }));
        refetchTeams();
        if (onSuccess) {
            onSuccess(t('pro_team_member.team_updated'));
        }
    };

    const handleDeleteTeamSuccess = () => {
        setIsDeleteTeamDialogOpen(false);
        setSelectedTeamForDelete(null);
        // Remove from locally created teams to reflect deletion immediately
        setCreatedTeams(prev => prev.filter(t => {
            const id = t.team_id || t.id;
            const delId = selectedTeamForDelete?.team_id || selectedTeamForDelete?.id;
            return String(id) !== String(delId);
        }));
        refetchTeams();
        if (onSuccess) {
            onSuccess(t('pro_team_member.team_deleted'));
        }
    };

    const handleCreatePlanning = (member) => {
        // This will be implemented to open a planning creation dialog
        if (onSuccess) {
            onSuccess(t('pro_team_member.planning_created', { member: member.user_details?.first_name || t('common.member') }));
        }
    };

    const renderEmployeesView = () => {
        return (
            <Box sx={{ p: 0, minHeight: '400px' }}>
                {selectedSalonId && (
                    <TeamMembers 
                        salonId={selectedSalonId} 
                        createdTeams={createdTeams}
                        onManageUnavailability={canManageAbsences ? (member) => {
                            setSelectedAbsenceMember(member);
                            setViewMode('unavailabilities');
                        } : undefined}
                        onTeamDeleted={(deletedTeam) => {
                            if (!deletedTeam) return;
                            const delId = deletedTeam.team_id || deletedTeam.id;
                            setCreatedTeams(prev => prev.filter(t => String(t.team_id || t.id) !== String(delId)));
                        }}
                    />
                )}
            </Box>
        );
    };

    const renderUnavailabilitiesView = () => (
        <Box sx={{ p: { xs: 0, md: 2 }, minHeight: '400px' }}>
            <EmployeeUnavailabilityPanel
                salonId={selectedSalonId}
                members={allMembers}
                preselectedMember={selectedAbsenceMember}
            />
        </Box>
    );

    const renderPlanningsView = () => {
        return (
            <Box sx={{ p: 0, minHeight: '400px' }}>
                {selectedSalonId && (
                    <Schedule salonId={selectedSalonId} />
                )}
            </Box>
        );
    };

    const renderTeamsView = () => {
        return (
            <Box sx={{ p: 3, minHeight: '400px' }}>
                {allTeams.length > 0 ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {allTeams.map((team, index) => {
                            const teamId = team.team_id || team.id;
                            const teamMembers = allMembers.filter(
                                member => member.team === teamId || member.team_id === teamId
                            );
                            
                            return (
                                <Card key={teamId || index} sx={{ 
                                    width: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    background: "linear-gradient(145deg, #ffffff 0%, #fdfdfd 100%)",
                                    border: `1px solid ${theme.colors.border.light}`,
                                    borderRadius: "12px",
                                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                    position: "relative",
                                    overflow: "hidden",
                                    "&::before": {
                                        content: '""',
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        height: "3px",
                                        background: `linear-gradient(90deg, ${theme.colors.primary[400]}, ${theme.colors.secondary[300]})`,
                                    },
                                    "&:hover": {
                                        transform: "translateY(-4px)",
                                        boxShadow: `0 12px 28px rgba(65, 101, 72, 0.12)`,
                                        borderColor: theme.colors.primary[200],
                                        "& .team-actions": {
                                            opacity: 1,
                                            transform: "translateY(0)",
                                        },
                                    },
                                }}>
                                    <CardContent sx={{
                                        flexGrow: 1,
                                        display: "flex",
                                        flexDirection: "column",
                                        padding: "16px",
                                        gap: "12px",
                                        "&:last-child": {
                                            paddingBottom: "16px",
                                        },
                                    }}>
                                        {/* Team Header */}
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexGrow: 1 }}>
                                                <Typography
                                                    variant="h6"
                                                    sx={{
                                                        fontWeight: 700,
                                                        color: theme.colors.primary[700],
                                                        fontSize: "16px",
                                                        lineHeight: "1.2",
                                                        display: "-webkit-box",
                                                        WebkitLineClamp: 1,
                                                        WebkitBoxOrient: "vertical",
                                                        overflow: "hidden",
                                                    }}
                                                >
                                                    {team.name}
                                                </Typography>
                                                
                                                {/* Edit and Delete Icons */}
                                                <Box className="team-actions" sx={{
                                                    display: "flex",
                                                    gap: "2px",
                                                    opacity: 0.7,
                                                    transform: "translateY(2px)",
                                                    transition: "all 0.3s ease",
                                                }}>
                                                    <Tooltip title={t('pro_team_member.edit')} arrow>
                                                        <IconButton
                                                            onClick={() => handleEditTeam(team)}
                                                            sx={{
                                                                padding: "4px",
                                                                borderRadius: "4px",
                                                                backgroundColor: "transparent",
                                                                transition: "all 0.2s ease",
                                                                "&:hover": {
                                                                    backgroundColor: theme.colors.primary[50],
                                                                    transform: "scale(1.05)",
                                                                },
                                                            }}
                                                        >
                                                            <EditIcon
                                                                sx={{
                                                                    fontSize: "14px",
                                                                    color: theme.colors.primary[600],
                                                                }}
                                                            />
                                                        </IconButton>
                                                    </Tooltip>
                                                    <Tooltip title={t('pro_team_member.delete')} arrow>
                                                        <IconButton
                                                            onClick={() => handleDeleteTeam(team)}
                                                            sx={{
                                                                padding: "4px",
                                                                borderRadius: "4px",
                                                                backgroundColor: "transparent",
                                                                transition: "all 0.2s ease",
                                                                "&:hover": {
                                                                    backgroundColor: theme.colors.error[50],
                                                                    transform: "scale(1.05)",
                                                                },
                                                            }}
                                                        >
                                                            <DeleteIcon
                                                                sx={{
                                                                    fontSize: "14px",
                                                                    color: theme.colors.error[500],
                                                                }}
                                                            />
                                                        </IconButton>
                                                    </Tooltip>
                                                </Box>
                                            </Box>
                                            
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<AddIcon />}
                                                onClick={() => handleOpenMemberDialog(team)}
                                                sx={{
                                                    borderColor: theme.colors.primary[300],
                                                    color: theme.colors.primary[600],
                                                    '&:hover': {
                                                        borderColor: theme.colors.primary[500],
                                                        backgroundColor: theme.colors.primary[50]
                                                    }
                                                }}
                                            >
                                                {t('pro_team_member.add_member_btn')}
                                            </Button>
                                        </Box>
                                        
                                        {/* Description */}
                                        {team.description && (
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: theme.colors.text.secondary,
                                                    lineHeight: "1.4",
                                                    fontSize: "13px",
                                                    textAlign: "justify",
                                                    textJustify: "inter-word",
                                                    width: "100%",
                                                    wordBreak: "break-word",
                                                    hyphens: "auto",
                                                    overflow: "hidden",
                                                    display: "-webkit-box",
                                                    WebkitBoxOrient: "vertical",
                                                    WebkitLineClamp: "unset",
                                                    maxHeight: "60px",
                                                    minHeight: "40px",
                                                }}
                                            >
                                                {team.description !== "-" ? team.description : t('pro_team_member.no_description_available')}
                                            </Typography>
                                        )}
                                        
                                        {/* Team members */}
                                        {teamMembers.length > 0 && (
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                                    {t('pro_team_member.members_count', { count: teamMembers.length })}
                                                </Typography>
                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                    {teamMembers.map((member, memberIndex) => {
                                                        const userDetails = member.user_details || member.user || {};
                                                        return (
                                                            <Chip
                                                                key={memberIndex}
                                                                avatar={
                                                                    <Avatar sx={{ 
                                                                        bgcolor: theme.colors.secondary[500],
                                                                        width: 24,
                                                                        height: 24
                                                                    }}>
                                                                        {userDetails.first_name?.charAt(0) || 'M'}
                                                                    </Avatar>
                                                                }
                                                                label={`${userDetails.first_name} ${userDetails.last_name}`}
                                                                sx={{
                                                                    backgroundColor: theme.colors.secondary[100],
                                                                    color: theme.colors.secondary[700],
                                                                    fontSize: '0.8rem'
                                                                }}
                                                            />
                                                        );
                                                    })}
                                                </Box>
                                            </Box>
                                        )}
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </Box>
                ) : (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                        <TeamIcon sx={{ fontSize: 64, color: theme.colors.primary[300], mb: 2 }} />
                        <Typography variant="h6" sx={{ color: theme.colors.primary[500], mb: 1 }}>
                            {t('pro_team_member.no_teams_created')}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t('pro_team_member.create_teams_organize')}
                        </Typography>
                    </Box>
                )}
            </Box>
        );
    };

    return (
        <>
            {/* Team Management Dialog */}
            <Dialog
                open={showTeamManagement}
                onClose={handleCloseTeamManagement}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        minHeight: '600px'
                    }
                }}
            >
                <DialogTitle sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    pb: 1
                }}>
                    <Typography variant="h6">
                        {t('pro_team_member.team_management')}
                    </Typography>
                    <IconButton onClick={handleCloseTeamManagement} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 0 }}>
                    <Team 
                        salonId={selectedSalonId}
                        onSuccess={(message) => {
                            if (onSuccess) {
                                onSuccess(message || t('pro_team_member.team_updated'));
                            }
                        }}
                    />
                </DialogContent>
            </Dialog>

            {/* Main Content */}
            <Card sx={{ 
                mb: 4, 
                borderRadius: theme.borderRadius.xl,
                boxShadow: `0 4px 20px ${theme.colors.primary[50]}`,
                border: `1px solid ${theme.colors.primary[100]}`,
            }}>
                <CardContent sx={{ p: 4 }}>
                    <Typography 
                        variant="h5" 
                        sx={{ 
                            mb: 3, 
                            display: 'flex', 
                            alignItems: 'center',
                            color: theme.colors.primary[700],
                            fontWeight: 'bold',
                            textAlign: 'center',
                            justifyContent: 'center',
                            fontSize: { xs: '1.25rem', md: '1.5rem' }
                        }}
                    >
                        <TeamIcon sx={{ 
                            mr: 1.5, 
                            color: theme.colors.primary[500],
                            fontSize: { xs: '1.5rem', md: '2rem' }
                        }} />
                        {t('pro_team_member.teams_and_members')}
                    </Typography>

                    {/* Action Buttons */}
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, justifyContent: 'center', mb: 3, alignItems: 'center' }}>
                        <Button
                            variant="contained"
                            startIcon={<TeamIcon />}
                            onClick={handleOpenTeamDialog}
                            sx={{
                                backgroundColor: theme.colors.primary[500],
                                color: theme.colors.primary[50],
                                px: { xs: 2, md: 3 },
                                py: { xs: 1.2, md: 1.5 },
                                borderRadius: theme.borderRadius.lg,
                                fontSize: { xs: '0.875rem', md: '1rem' },
                                width: { xs: '100%', md: 'auto' },
                                maxWidth: { xs: '300px', md: 'none' },
                                '&:hover': {
                                    backgroundColor: theme.colors.primary[600],
                                    transform: 'translateY(-2px)'
                                },
                                transition: 'all 0.2s ease'
                            }}
                        >
                            {t('pro_team_member.create_team')}
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<MemberIcon />}
                            onClick={() => handleOpenMemberDialog(null)}
                            sx={{
                                backgroundColor: theme.colors.secondary[500],
                                color: theme.colors.primary[50],
                                px: { xs: 2, md: 3 },
                                py: { xs: 1.2, md: 1.5 },
                                borderRadius: theme.borderRadius.lg,
                                fontSize: { xs: '0.875rem', md: '1rem' },
                                width: { xs: '100%', md: 'auto' },
                                maxWidth: { xs: '300px', md: 'none' },
                                '&:hover': {
                                    backgroundColor: theme.colors.secondary[600],
                                    transform: 'translateY(-2px)'
                                },
                                transition: 'all 0.2s ease'
                            }}
                        >
                            {t('pro_team_member.create_member_planning')}
                        </Button>
                    </Box>

                    {/* View Toggle Tabs */}
                    <Box sx={{ 
                        mb: 3, 
                        display: 'flex', 
                        justifyContent: 'center',
                        width: '100%',
                        overflow: 'hidden'
                    }}>
                        <Tabs 
                            value={viewMode} 
                            onChange={(e, newValue) => setViewMode(newValue)}
                            variant={isMobile ? "scrollable" : "standard"}
                            scrollButtons={isMobile ? "auto" : false}
                            allowScrollButtonsMobile={isMobile}
                            sx={{
                                width: '100%',
                                maxWidth: isMobile ? '100%' : 'auto',
                                '& .MuiTabs-indicator': {
                                    backgroundColor: theme.colors.primary[500],
                                },
                                '& .MuiTabs-root': {
                                    minHeight: isMobile ? 48 : 'auto'
                                },
                                '& .MuiTabs-flexContainer': {
                                    justifyContent: isMobile ? 'flex-start' : 'center'
                                },
                                '& .MuiTab-root': {
                                    color: theme.colors.text.secondary,
                                    minWidth: isMobile ? 120 : 'auto',
                                    fontSize: isMobile ? '0.8rem' : '0.875rem',
                                    padding: isMobile ? '8px 12px' : '12px 16px',
                                    '&.Mui-selected': {
                                        color: theme.colors.primary[600],
                                        fontWeight: 'bold'
                                    }
                                },
                                '& .MuiSvgIcon-root': {
                                    fontSize: isMobile ? '1rem' : '1.2rem'
                                }
                            }}
                        >
                            <Tab 
                                label={isMobile ? t('pro_team_member.view_employee_mobile') : t('pro_team_member.view_employee')} 
                                value="employees" 
                                icon={<EmployeeIcon />} 
                                iconPosition={isMobile ? "top" : "start"}
                            />
                            <Tab 
                                label={isMobile ? t('pro_team_member.view_plannings_mobile') : t('pro_team_member.view_plannings')} 
                                value="plannings" 
                                icon={<CalendarIcon />} 
                                iconPosition={isMobile ? "top" : "start"}
                            />
                            {canManageAbsences && (
                                <Tab
                                    label={isMobile ? 'Absences' : 'Congés et absences'}
                                    value="unavailabilities"
                                    icon={<AbsenceIcon />}
                                    iconPosition={isMobile ? "top" : "start"}
                                />
                            )}
                            {/* <Tab 
                                label={isMobile ? "Équipes" : "Vue équipe"} 
                                value="teams" 
                                icon={<TeamIcon />} 
                                iconPosition={isMobile ? "top" : "start"}
                            /> */}
                        </Tabs>
                    </Box>

                    {/* Render content based on view mode */}
                    {viewMode === 'employees' && renderEmployeesView()}
                    {viewMode === 'plannings' && renderPlanningsView()}
                    {viewMode === 'unavailabilities' && canManageAbsences && renderUnavailabilitiesView()}
                    {viewMode === 'teams' && renderTeamsView()}
                </CardContent>
            </Card>

            {/* Sequential Dialogs - Only one shows at a time */}
            {currentDialog?.type === 'team' && (
                <AddTeam {...currentDialog.props} />
            )}
            
            {currentDialog?.type === 'member' && (
                <AddMember {...currentDialog.props} />
            )}
            
            {currentDialog?.type === 'planning' && (
                <Schedule 
                    {...currentDialog.props}
                    open={true}
                    onClose={closeDialog}
                    memberId={currentDialog.props.member?.team_member_id}
                    salonId={selectedSalonId}
                />
            )}
            
            {/* Update Team Dialog */}
            {selectedTeamForEdit && (
                <UpdateTeam
                    open={isUpdateTeamDialogOpen}
                    onClose={() => setIsUpdateTeamDialogOpen(false)}
                    onSuccess={handleUpdateTeamSuccess}
                    team={selectedTeamForEdit}
                />
            )}
            
            {/* Delete Team Dialog */}
            {selectedTeamForDelete && (
                <DeleteTeam
                    open={isDeleteTeamDialogOpen}
                    onClose={() => {
                        setIsDeleteTeamDialogOpen(false);
                        setSelectedTeamForDelete(null);
                    }}
                    onSuccess={handleDeleteTeamSuccess}
                    team={{
                        ...selectedTeamForDelete,
                        id: selectedTeamForDelete.team_id || selectedTeamForDelete.id
                    }}
                />
            )}
        </>
    );
};

export default MemberPlanningSectionSequential;
